import { memo } from "react";
import { PressableScale } from "pressto";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { AlarmStateBadge } from "./AlarmStateBadge";
import { formatRoutineForDisplay } from "@src/features/alarms/utils/format-routine";
import type { FC } from "react";
import type { AlarmRoutine } from "@src/services/api/types";

export interface ProcessedSuggestion {
  name: string | null;
  routine: AlarmRoutine;
  action: "armed" | "disarmed";
}

interface CommonRoutinesProps {
  suggestions: ProcessedSuggestion[];
  onSelect: (routine: AlarmRoutine) => void;
}

export const CommonRoutines: FC<CommonRoutinesProps> = memo(({ suggestions, onSelect }) => {
  const { theme } = useUnistyles();

  if (suggestions.length === 0) return null;

  return (
    <View px="5xl" mt="7xl" flex={1}>
      <Text textStyle="xs/medium" color="textMuted" mb="lg">
        Common routines
      </Text>

      {suggestions.map((suggestion, index) => (
        <PressableScale
          key={index}
          onPress={() => onSelect(suggestion.routine)}
          style={{ marginBottom: theme.spacing.lg }}
        >
          <View bg="surface" p="4xl" rounded={8} borderWidth={1} borderColor="border">
            <View flexDirection="row" justifyContent="space-between">
              <View flex={1}>
                <View flexDirection="row" alignItems="center" gap="md" mb="2xl">
                  {suggestion.name && (
                    <Text textStyle="sm/medium" color="text">
                      {suggestion.name}
                    </Text>
                  )}
                  <AlarmStateBadge state={suggestion.action} />
                </View>

                {formatRoutineForDisplay(suggestion.routine).map((line, lineIndex) => (
                  <View key={lineIndex} flexDirection="row" alignItems="center" gap="sm" mb="md">
                    <Icon name="calendarOutline" color={theme.colors.textMuted} size={16} />
                    <Text textStyle="xs/regular" color="textMuted">
                      {line.name}
                    </Text>
                    {line.timeRanges && (
                      <>
                        <Text textStyle="xs/regular" color="textMuted">
                          •
                        </Text>
                        <Icon name="clockOutline" color={theme.colors.textMuted} size={16} />
                        <Text textStyle="xs/regular" color="textMuted">
                          {line.timeRanges}
                        </Text>
                      </>
                    )}
                  </View>
                ))}
              </View>
              <Icon name="edit" color={theme.colors.textMuted} style={{ marginLeft: theme.spacing["3xl"] }} />
            </View>
          </View>
        </PressableScale>
      ))}
    </View>
  );
});

CommonRoutines.displayName = "CommonRoutines";
